import { map, uniq, zipObject } from 'lodash';
import { getNullSelectOption, SelectOption, SelectOptions } from '../../../shared/form/inputs/Select';
import { ManifoldFormModel } from './manifolds/manifoldFormModel';

export const floorLevelCodes = [
  'Basement',
  'LowerGround',
  'UpperGround',
  'Ground',
  'Floor1',
  'Floor2',
  'Floor3',
  'Floor4',
  'Floor5',
  'Floor6',
  'Floor7',
  'Floor8',
  'Floor9',
] as const;

export type FloorLevelCode = typeof floorLevelCodes[number];

export const floorLevels = ['B', 'LG', 'UG', 'G', '1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;

export const floorLevelsByFloorLevelCode = zipObject(floorLevelCodes, floorLevels);

export const floorLevelSelectOptions: SelectOptions<FloorLevelCode | null> = [
  getNullSelectOption<FloorLevelCode>(),
  ...floorLevels.map((floorName, index) => ({
    text: floorName,
    value: floorLevelCodes[index],
  })),
];

export const getFloorLevelOrderingPosition = (floorLevelCode: FloorLevelCode | null): number =>
  floorLevelCode ? floorLevelCodes.indexOf(floorLevelCode) : floorLevelCodes.length;

export const thermostatTypeCodes = [
  'Dial',
  'NeoWhite',
  'NeoSilver',
  'NeoBlack',
  'NeoAirWhite',
  'NeoAirSilver',
  'NeoAirBlack',
  'ElectricWhite',
  'ElectricSilver',
  'ElectricBlack',
  'UserSupplied',
  'Shared',
] as const;
export type ThermostatTypeCode = typeof thermostatTypeCodes[number];

export const allowedThermostatTypeCodesForElectricUnderfloorHeating: Array<ThermostatTypeCode> = [
  'ElectricWhite',
  'ElectricSilver',
  'ElectricBlack',
  'UserSupplied',
  'Shared',
];

export const thermostatTypeCodesForElectricUnderfloorHeatingOnly: Array<ThermostatTypeCode> = [
  'ElectricWhite',
  'ElectricSilver',
  'ElectricBlack',
];

export const thermostatTypeCodesForOneSensorOnly: Array<ThermostatTypeCode> = [
  'Dial',
  'NeoAirWhite',
  'NeoAirSilver',
  'NeoAirBlack',
];

const thermostatTypes = [
  'Dial',
  'neoStat (white)',
  'neoStat (silver)',
  'neoStat (black)',
  'Neo Air (white)',
  'Neo Air (silver)',
  'Neo Air (black)',
  'Electric (white)',
  'Electric (silver)',
  'Electric (black)',
  'User supplied',
  'Shared',
] as const;
type ThermostatType = typeof thermostatTypes[number];

const thermostatTypesByThermostatTypeCode: Record<ThermostatTypeCode, ThermostatType> = {
  Dial: 'Dial',
  NeoWhite: 'neoStat (white)',
  NeoSilver: 'neoStat (silver)',
  NeoBlack: 'neoStat (black)',
  NeoAirWhite: 'Neo Air (white)',
  NeoAirSilver: 'Neo Air (silver)',
  NeoAirBlack: 'Neo Air (black)',
  ElectricWhite: 'Electric (white)',
  ElectricSilver: 'Electric (silver)',
  ElectricBlack: 'Electric (black)',
  UserSupplied: 'User supplied',
  Shared: 'Shared',
};

const toThermostatSelectOption = (code: ThermostatTypeCode): SelectOption<ThermostatTypeCode> => ({
  value: code,
  text: thermostatTypesByThermostatTypeCode[code],
});

export const thermostatSelectOptions: SelectOptions<ThermostatTypeCode> = thermostatTypeCodes.map(
  toThermostatSelectOption,
);

export const nonSharedThermostatSelectOptions: SelectOptions<ThermostatTypeCode> = thermostatTypeCodes
  .filter(code => code !== 'Shared')
  .map(toThermostatSelectOption);

export const getQuoteRoomThermostatSelectOptions = (
  quoteThermostatTypeCode: ThermostatTypeCode,
  roomThermostatTypeCode: ThermostatTypeCode,
): SelectOptions<ThermostatTypeCode> =>
  uniq<ThermostatTypeCode>([quoteThermostatTypeCode, 'Shared', roomThermostatTypeCode]).map(toThermostatSelectOption);

export const getQuoteRoomlessManifoldThermostatSelectOptions = (
  quoteThermostatTypeCode: ThermostatTypeCode,
  roomlessManifoldThermostatTypeCode: ThermostatTypeCode,
): SelectOptions<ThermostatTypeCode> =>
  uniq([quoteThermostatTypeCode, roomlessManifoldThermostatTypeCode]).map(toThermostatSelectOption);

export const getThermostatSharedWithOptions = (manifold: ManifoldFormModel): SelectOptions<string | null> => [
  getNullSelectOption<string>(),
  ...(manifold.rooms ?? [])
    .filter(({ thermostatTypeCode }) => thermostatTypeCode !== 'Shared')
    .map(({ name, id }) => ({ text: name, value: id })),
];

export const unitHeatSourceTypeCodes = ['HeatPump', 'Boiler'] as const;
export type UnitHeatSourceTypeCode = typeof unitHeatSourceTypeCodes[number];

export const unitHeatSourceTypeDisplayNames: { [typeCode in UnitHeatSourceTypeCode]: string } = {
  HeatPump: 'Heat Pump',
  Boiler: 'Boiler',
};

export const pumpSelectionTypeCodes = ['Automatic', 'UserDefined', 'PumpsNotSupplied'] as const;
export type PumpSelectionTypeCode = typeof pumpSelectionTypeCodes[number];

export const pumpSelectionTypes = ['Automatic', 'User defined', 'Pumps not supplied'];

export const pumpSelectionTypeSelectOptions: SelectOptions<PumpSelectionTypeCode> = map(
  pumpSelectionTypeCodes,
  (code, index) => ({
    value: code,
    text: pumpSelectionTypes[index],
  }),
);

export const pumpLocationCodes = ['RemotePump', 'PumpOnManifold'] as const;
export type PumpLocationCode = typeof pumpLocationCodes[number];

export const pumpLocations = ['Remote Pump', 'Pump on Manifold'] as const;

export const pumpLocationSelectOptions: SelectOptions<PumpLocationCode> = map(pumpLocationCodes, (code, index) => ({
  value: code,
  text: pumpLocations[index],
}));

export const heatPumpTypeCodes = ['AirSource', 'GroundSourceWithGroundLoops', 'GroundSourceWithBorehole'] as const;
export type HeatPumpTypeCode = typeof heatPumpTypeCodes[number];

export const heatPumpTypeSelectOptions: SelectOptions<HeatPumpTypeCode> = [
  { value: 'AirSource', text: 'Air source' },
  { value: 'GroundSourceWithGroundLoops', text: 'Ground source with ground loops' },
  { value: 'GroundSourceWithBorehole', text: 'Ground source with borehole' },
];

export const groundCollectorTypeCodes = ['TwentyFiveMillimetres', 'FortyMillimetres'] as const;
export type GroundCollectorTypeCode = typeof groundCollectorTypeCodes[number];

export const groundCollectorTypeOptions: SelectOptions<GroundCollectorTypeCode> = [
  { value: 'TwentyFiveMillimetres', text: '25mm' },
  { value: 'FortyMillimetres', text: '40mm' },
];

export const swimmingPoolTypeCodes = ['None', 'Indoor', 'Outdoor'] as const;
export type SwimmingPoolTypeCode = typeof swimmingPoolTypeCodes[number];

export const swimmingPoolTypeOptions: SelectOptions<SwimmingPoolTypeCode> = [
  { value: 'None', text: 'No swimming pool' },
  { value: 'Indoor', text: 'Indoor swimming pool' },
  { value: 'Outdoor', text: 'Outdoor swimming pool' },
];
